"use client";

import { Button, Card, IconButton } from "@mui/material";
import useCart from "@/stores/cart";
import useNumber from "@/hooks/number";
import Link from "next/link";
import routes from "@/app.route";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import Image from "next/image";
import SparkleEffect from "@/components/sparkle-effect";
import { getWithGstAmount } from "@/lib/gst";

export default function Cart() {
  const { currency } = useNumber();
  const { items, increment, decrement, removeItem } = useCart();
  const total = items.reduce(
    (acc, i) =>
      acc + getWithGstAmount(i.price, i.gst_rate) * Number(i.quantity),
    0,
  );

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-4xl">My Shopping Cart</h1>
      </div>
      <div className="max-w-7xl mx-auto px-4 mb-8">
        <Card>
          <div className="py-4 items-center border-b border-zinc-200 md:flex hidden">
            <div className="text-zinc-500 px-4 flex-1">Product</div>
            <div className="text-zinc-500 px-4 w-36">Price</div>
            <div className="text-zinc-500 px-4 w-36">Quantity</div>
            <div className="text-zinc-500 px-4 w-36">Subtotal</div>
            <div className="text-zinc-500 px-4 w-36"></div>
          </div>
          {items.map((variant, i) => (
            <div
              key={i}
              className="flex py-4 items-center border-b border-zinc-200"
            >
              <div className="flex-1 px-4">
                <Link
                  href={routes.product.url.replace(
                    ":slug",
                    variant.product.handle,
                  )}
                  className="inline-flex gap-x-4 items-center hover:text-primary"
                >
                  {variant.product.featured_image && (
                    <Image
                      src={variant.product.featured_image.original_url}
                      alt={variant.product.title}
                      width={128}
                      height={128}
                    />
                  )}
                  <div className="font-medium">{variant.product.title}</div>
                </Link>
              </div>
              <div className="w-36 px-4">
                {currency(getWithGstAmount(variant.price, variant.gst_rate))}
                {variant.compare_at_price && (
                  <>
                    <br />
                    <span className="line-through text-sm text-zinc-500">
                      {currency(
                        getWithGstAmount(
                          variant.compare_at_price,
                          variant.gst_rate,
                        ),
                      )}
                    </span>
                  </>
                )}
              </div>
              <div className="w-36 px-4">
                <div className="border border-zinc-400 rounded overflow-hidden flex flex-nowrap">
                  <button
                    disabled={Number(variant.quantity) == 1}
                    onClick={() => decrement(`${variant.id}`)}
                    type="button"
                    className={`bg-zinc-50 shrink-0 size-9 grid place-items-center border-r border-zinc-400 ${Number(variant.quantity) == 1 ? "cursor-no-drop" : "cursor-pointer"}`}
                  >
                    <RemoveRoundedIcon fontSize="small" />
                  </button>
                  <div className="size-9 grid place-items-center px-1">
                    {variant.quantity}
                  </div>
                  <button
                    onClick={() => increment(`${variant.id}`)}
                    type="button"
                    className="bg-zinc-50 shrink-0 size-9 grid place-items-center border-l border-zinc-400 cursor-pointer"
                  >
                    <AddRoundedIcon fontSize="small" />
                  </button>
                </div>
              </div>
              <div className="w-36 px-4">
                {currency(
                  getWithGstAmount(variant.price, variant.gst_rate) *
                    Number(variant.quantity),
                )}
              </div>
              <div className="w-36 px-4 flex justify-end">
                <IconButton
                  onClick={() => {
                    removeItem(`${variant.id}`);
                  }}
                >
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              </div>
            </div>
          ))}
        </Card>
      </div>
      <div className="bg-white sticky bottom-0 p-4 border-t border-zinc-200 z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-2xl">
                Cart Total: <strong>{currency(total)}</strong>
              </div>
              <div className="text-zinc-500 text-sm">
                Shipping: Calculate at Checkout
              </div>
            </div>
            <div>
              <SparkleEffect>
                <Button href={routes.checkout.url} variant="contained">
                  Proceed to Checkout
                </Button>
              </SparkleEffect>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
